import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { login, loginTwoFactor } from '../lib/auth';
import { useAuth } from '../lib/auth-context';
import { errorMessage, hasCode } from '../lib/api-error';
import { firstAllowedPath } from '../lib/permissions';
import { Button, Card, Input } from '../components/ui';

type Step = 'credentials' | 'two-factor';

export function LoginPage() {
  const { user, adopt } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Zaten girişliyse giriş ekranı gösterilmez.
  if (user) return <Navigate to={firstAllowedPath(user) ?? '/'} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (step === 'credentials') {
        const outcome = await login(email.trim(), password);
        if (outcome.status === 'two-factor') {
          setStep('two-factor');
          return;
        }
        adopt(outcome.user);
        navigate(firstAllowedPath(outcome.user) ?? '/', { replace: true });
      } else {
        const session = await loginTwoFactor(email.trim(), password, code.trim());
        adopt(session);
        navigate(firstAllowedPath(session) ?? '/', { replace: true });
      }
    } catch (err) {
      /*
       * Şifre yanlışsa ikinci adımda kalmanın anlamı yok — sunucu 2FA yolunda
       * şifreyi YENİDEN doğrular ve INVALID_CREDENTIALS dönebilir. O durumda
       * kullanıcıyı ilk adıma geri al, yoksa neyi düzelteceğini anlayamaz.
       */
      if (step === 'two-factor' && hasCode(err, 'INVALID_CREDENTIALS')) {
        setStep('credentials');
        setCode('');
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--c-bg)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight">CentraMir</h1>
          <p className="text-xs text-[var(--c-ink-faint)]">Muhasebe &amp; faturalama</p>
        </div>

        <Card className="p-5">
          <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
            {step === 'credentials' ? (
              <>
                <Input
                  label="E-posta"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input
                  label="Şifre"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--c-ink-soft)]">
                  Doğrulama uygulamanızdaki 6 haneli kodu girin. Uygulamaya erişemiyorsanız yedek
                  kodlarınızdan birini kullanabilirsiniz.
                </p>
                <Input
                  label="Doğrulama kodu"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </>
            )}

            {error && (
              <p role="alert" className="text-sm text-[var(--c-danger)]">
                {error}
              </p>
            )}

            <Button type="submit" loading={busy} className="w-full">
              {step === 'credentials' ? 'Giriş yap' : 'Doğrula'}
            </Button>

            {step === 'two-factor' && (
              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setCode('');
                  setError(null);
                }}
                className="w-full text-xs text-[var(--c-ink-soft)] hover:underline"
              >
                Geri dön
              </button>
            )}
          </form>
        </Card>

        <p className="mt-4 text-center text-[11px] leading-relaxed text-[var(--c-ink-faint)]">
          Şifrenizi unuttuysanız sunucuda <code>pnpm --filter @centramir/api seed</code> ile
          kurtarma yapılabilir. Kendi kendine sıfırlama akışı bilerek yok — tek operatörlü bir
          panelde e-posta tabanlı sıfırlama, kazandığından fazla saldırı yüzeyi açar.
        </p>
      </div>
    </div>
  );
}
